"use client";

import { AnimatePresence, motion, useReducedMotion, type Variants } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import type { Locale } from "@/lib/i18n";
import { REVEAL_SCRIPT, type RevealLine } from "@/lib/reveal-script";

const HOLD_MS = 2600;
const easeOut = [0.22, 1, 0.36, 1] as const;

const baseClass =
  "font-sans text-3xl font-extralight leading-tight tracking-tight text-white dark:text-black sm:text-5xl md:text-6xl";

type Token = { text: string; em: boolean };

function toTokens(line: RevealLine): Token[] {
  const out: Token[] = [];
  const push = (s: string | undefined, em: boolean) => {
    if (!s) return;
    for (const w of s.trim().split(/\s+/)) if (w) out.push({ text: w, em });
  };
  push(line.pre, false);
  push(line.hi, true);
  push(line.post, false);
  return out;
}

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
  exit: { transition: { staggerChildren: 0.05, staggerDirection: -1 } },
};

/** Kelime tek tek soldan/sağdan uçarak gelir, blur'dan nete oturur; çıkışta ters yöne dağılır. */
function wordVariants(dir: number): Variants {
  return {
    hidden: { opacity: 0, x: dir * 80, filter: "blur(10px)" },
    show: {
      opacity: 1,
      x: 0,
      filter: "blur(0px)",
      transition: { duration: 0.55, ease: easeOut },
    },
    exit: {
      opacity: 0,
      x: dir * -60,
      filter: "blur(8px)",
      transition: { duration: 0.35, ease: easeOut },
    },
  };
}

export function HeroReveal({ locale }: { locale: Locale }) {
  const lines = REVEAL_SCRIPT[locale];
  const prefersReducedMotion = useReducedMotion();
  const [i, setI] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const id = window.setInterval(() => {
      setI((p) => (p + 1) % lines.length);
    }, HOLD_MS);
    return () => window.clearInterval(id);
  }, [lines.length, prefersReducedMotion]);

  const tokens = useMemo(() => toTokens(lines[i]), [lines, i]);

  if (prefersReducedMotion) {
    return (
      <p className={`relative z-10 max-w-[95vw] px-4 text-center ${baseClass}`}>
        {tokens.map((t, k) => (
          <span key={k} className={t.em ? "font-medium" : undefined}>
            {t.text}
            {k < tokens.length - 1 ? " " : ""}
          </span>
        ))}
      </p>
    );
  }

  return (
    <div className="relative z-10 flex min-h-[3.25rem] w-full items-center justify-center px-4 sm:min-h-[5rem] md:min-h-[5.5rem]">
      <AnimatePresence mode="wait">
        <motion.p
          key={i}
          className={`flex flex-wrap items-center justify-center gap-x-[0.3em] text-center ${baseClass}`}
          variants={container}
          initial="hidden"
          animate="show"
          exit="exit"
        >
          {tokens.map((t, k) => (
            <motion.span
              key={k}
              className={t.em ? "inline-block font-medium" : "inline-block"}
              variants={wordVariants(k % 2 === 0 ? -1 : 1)}
              style={{ willChange: "transform, opacity, filter" }}
            >
              {t.text}
            </motion.span>
          ))}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}
